import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogTitle,
  Button,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AdminNavbar from '../components/AdminNavbar';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../../config/api';
import Sidebar from '../components/Sidebar';

// BlogCard component with fixed height and truncated description
const BlogCard = ({ blog, onDelete }) => {
  const navigate = useNavigate();
  const { _id, title, description, image } = blog;
  const [openDialog, setOpenDialog] = useState(false);

  // A function to get a shortened description
  const getShortDescription = (text, maxLength) => {
    if (!text) return '';
    const plainText = text.replace(/(<([^>]+)>)/gi, ''); // Remove HTML tags
    if (plainText.length <= maxLength) {
      return plainText;
    }
    return plainText.substring(0, maxLength) + '...';
  };

  const handleDeleteClick = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);
  const handleConfirmDelete = () => {
    onDelete(_id);
    handleCloseDialog();
  };

  return (
    <>
      <div className="col-md-6 col-lg-4 col-sm-12 d-flex">
        <div className="property-card flex-grow-1 d-flex flex-column">
          <div
            className="property-image position-relative"
            onClick={() => navigate(`/admin/blog-detail/${_id}`)}
            style={{ cursor: 'pointer' }}
          >
            <img
              src={image}
              className="img-fluid"
              alt={title}
              loading="lazy"
            />
          </div>
          <div className="property-details flex-grow-1">
            <div className="d-flex justify-content-between my-2">
              <h5 className="mb-0">{title}</h5>
            </div>
            <p className="text-muted m-0 mb-2">
              {getShortDescription(description, 120)}
            </p>
          </div>
          <div className="admin-property-buttons mt-auto">
            <button
              className="btn me-1"
              onClick={() => navigate(`/admin/create-blog/${_id}`)}
              style={{ cursor: 'pointer' }}
            >
              <EditIcon style={{ marginRight: '7px' }} />
              Edit
            </button>
            <button
              className="btn"
              onClick={handleDeleteClick}
              style={{ cursor: 'pointer' }}
            >
              <DeleteIcon style={{ marginRight: '5px' }} />
              Delete
            </button>
          </div>
        </div>
      </div>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Are you sure you want to delete this blog?</DialogTitle>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Main AllBlogs component
const AllBlogs = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [sidebarOpen, setSidebarOpen] = useState(!isSmallScreen);
  const [blogs, setBlogs] = useState([]);
  const navigate = useNavigate();

  const toggleDrawer = () => setSidebarOpen(!sidebarOpen);

  const fetchBlogs = async () => {
    try {
      const res = await ApiService.get('blogs/list');
      if (!res.data.error) {
        setBlogs(res.data.meta);
      }
    } catch (err) {
      console.error('Error fetching blogs:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await ApiService.delete(`blogs/remove/${id}`);
      setBlogs((prev) => prev.filter((blog) => blog._id !== id));
    } catch (err) {
      console.error('Error deleting blog:', err);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  return (
    <Box
      display="grid"
      gridTemplateColumns={sidebarOpen ? "200px 1fr" : "60px 1fr"}
      sx={{
        transition: "grid-template-columns 0.3s ease-in-out",
        backgroundColor: '#f9f9f9',
        minHeight: '100vh',
      }}
    >
      <Sidebar open={sidebarOpen} toggleDrawer={toggleDrawer} setOpen={setSidebarOpen} />

      <Box>
        <AdminNavbar />
        <div style={{ backgroundColor: 'var(--bs-secondary)', minHeight: '100vh' }}>
          <div className="container py-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h4 style={{ color: 'var(--bs-primary)' }}>Blogs</h4>
              <Button
                variant="contained"
                style={{
                  backgroundColor: 'var(--bs-primary)',
                  color: 'white',
                  textTransform: 'none',
                }}
                onClick={() => navigate('/admin/create-blog')}
              >
                Create Blog
              </Button>
            </div>

            <div className="row g-4">
              {blogs.map((blog) => (
                <BlogCard key={blog._id} blog={blog} onDelete={handleDelete} />
              ))}
            </div>
          </div>
        </div>
      </Box>
    </Box>
  );
};

export default AllBlogs;